import {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import api, {getMutation, getQuery} from 'api';
import {getOrderRequestKey} from 'api/gifts/orderKey';
import arrowBackBlack from 'assets/img/arrowBackBlack.svg';
import {
  AddressBlock,
  AddressText,
  AddressTitle,
  BackButton,
  ButtonGroup,
  ConfirmationText,
  Container,
  Divider,
  EmptyState,
  ErrorBox,
  FieldError,
  FieldGroup,
  FieldLabel,
  FooterSpacer,
  FormBlock,
  FullWidthButtons,
  Header,
  PrimaryButton,
  ProductCard,
  ProductDetails,
  ProductImage,
  ProductMeta,
  ProductName,
  ProgressDot,
  ProgressDots,
  ProgressLabel,
  ProgressLabels,
  ProgressLine,
  ProgressLineActive,
  ProgressTrack,
  ProgressWrapper,
  RequiredHint,
  SecondaryButton,
  SummaryBlock,
  SummaryLabel,
  SummaryRow,
  SummaryValue,
  TextField,
  Title,
  TitleBlock,
} from './styles';

const steps = ['summary', 'shipping', 'confirmation'];
const selectGiftsState = state => getQuery(state, api.getGifts);
const selectProfileState = state => getQuery(state, api.getProfile);
const selectPostGiftOrderState = state => getMutation(state, api.postGiftOrder);

const stepTitles = {
  summary: 'Votre commande',
  shipping: 'Adresse de livraison',
  confirmation: 'Validation de commande',
};

// Longueurs acceptées par le prestataire de livraison.
const TEXT_MAX_LENGTH = 38;
const POSTAL_CODE_LENGTH = 5;
const PHONE_INPUT_MAX_LENGTH = 20;

// Le numéro peut être saisi avec ses séparateurs : ils sont retirés avant l'envoi,
// l'indicatif international étant ramené au format national.
const normalizePhone = value =>
  (value || '').replace(/[\s()./-]+/g, '').replace(/^(?:\+33|0033)0?/, '0');

// Dernière adresse de livraison utilisée, à défaut celle de l'établissement.
const mapProfileToShippingAddress = profile => ({
  lastName: profile?.shipping_address?.last_name || profile?.last_name || '',
  firstName: profile?.shipping_address?.first_name || profile?.first_name || '',
  establishment: profile?.shipping_address?.establishment || profile?.establishment || '',
  address: profile?.shipping_address?.address || profile?.address || '',
  city: profile?.shipping_address?.city || profile?.city || '',
  postalCode: profile?.shipping_address?.postal_code || profile?.postal_code || '',
  phone: profile?.shipping_address?.phone || profile?.phone || '',
});

const validateAddress = values => {
  const errors = {};

  const textFields = [
    ['lastName', 'Le nom est requis', 'Le nom'],
    ['firstName', 'Le prénom est requis', 'Le prénom'],
    ['establishment', "Le nom de l'établissement est requis", "Le nom de l'établissement"],
    ['address', "L'adresse de livraison est requise", "L'adresse de livraison"],
    ['city', 'La ville de livraison est requise', 'La ville de livraison'],
  ];

  textFields.forEach(([field, requiredMessage, label]) => {
    const value = (values[field] || '').trim();

    if (!value) {
      errors[field] = requiredMessage;
    } else if (value.length > TEXT_MAX_LENGTH) {
      errors[field] = `${label} ne doit pas dépasser ${TEXT_MAX_LENGTH} caractères`;
    }
  });

  const postalCode = (values.postalCode || '').trim();
  if (!postalCode) {
    errors.postalCode = 'Le code postal est requis';
  } else if (!/^\d{5}$/.test(postalCode)) {
    errors.postalCode = `Le code postal doit comporter exactement ${POSTAL_CODE_LENGTH} chiffres`;
  }

  const phone = normalizePhone(values.phone);
  if (!phone) {
    errors.phone = 'Le téléphone du contact de livraison est requis';
  } else if (!/^0[1-9]\d{8}$/.test(phone)) {
    errors.phone = 'Le téléphone doit comporter 10 chiffres (exemple : 0601020304)';
  }

  return errors;
};

const sanitizeGiftLabel = value => value?.replace(/&#8217;/g, '’').replace(/&rsquo;/gi, '’');

const buildStepPath = (giftId, stepName) => `/gifts/order/${giftId}/${stepName}`;

const GiftOrderFlow = () => {
  const {giftId, step = 'summary'} = useParams();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const orderSubmitted = location.state?.orderSubmitted === true;

  const giftsState = useSelector(selectGiftsState);
  const profileState = useSelector(selectProfileState);
  const {loading: mutationLoading, error: mutationError} = useSelector(selectPostGiftOrderState);

  const [shippingAddress, setShippingAddress] = useState(mapProfileToShippingAddress());
  const [addressErrors, setAddressErrors] = useState({});
  const [activeGiftId, setActiveGiftId] = useState(null);
  const [submissionError, setSubmissionError] = useState('');

  useEffect(() => {
    dispatch(api.getGifts());
    dispatch(api.getProfile());
  }, [dispatch]);

  useEffect(() => {
    if (!profileState.data) return;
    if (activeGiftId === giftId) return;

    setShippingAddress(mapProfileToShippingAddress(profileState.data));
    setAddressErrors({});
    setActiveGiftId(giftId);
  }, [activeGiftId, giftId, profileState.data]);

  const stepIndex = steps.indexOf(step);

  useEffect(() => {
    if (stepIndex === -1) {
      history.replace(buildStepPath(giftId, 'summary'));
    }
  }, [giftId, history, stepIndex]);

  const availablePoints = process.env.REACT_APP_DEV_POINTS
    ? Number(process.env.REACT_APP_DEV_POINTS)
    : Number(profileState.data?.points || 0);

  const gift = useMemo(
    () => giftsState.data?.find(item => String(item.id) === giftId),
    [giftId, giftsState.data],
  );

  const requiredPoints = Number(gift?.acf?.cadeaux_points || 0);
  const remainingPoints = availablePoints - requiredPoints;
  // Après la commande, le solde fait foi côté serveur ; le calcul local sert de repli.
  const balanceAfterOrder = Number.isFinite(location.state?.remainingPoints)
    ? location.state.remainingPoints
    : remainingPoints;
  const isAffordable = gift && requiredPoints <= availablePoints;

  useEffect(() => {
    if (gift && !isAffordable) {
      history.replace('/gifts');
    }
  }, [gift, history, isAffordable]);

  const mutationRequestError = useMemo(() => {
    if (!mutationError) return [];
    if (mutationError?.request?.response) {
      try {
        const response = JSON.parse(mutationError.request.response);
        const serverError = response?.error?.message || response?.message || response?.code;
        const reference = response?.order_id ? ` Référence : ${response.order_id}` : '';
        return serverError ? [`${serverError}${reference}`] : [];
      } catch {
        return [];
      }
    }
    return mutationError?.response?.data?.code ? [mutationError.response.data.code] : [];
  }, [mutationError]);

  const goToStep = nextStep => history.push(buildStepPath(giftId, nextStep));

  const handleBack = () => {
    if (step === 'shipping') {
      goToStep('summary');
      return;
    }
    if (step === 'confirmation') {
      if (orderSubmitted) {
        history.push('/');
        return;
      }
      goToStep('shipping');
      return;
    }
    history.push('/gifts');
  };

  const handleAddressChange = field => event => {
    const {value} = event.target;
    setShippingAddress(current => ({...current, [field]: value}));
    setAddressErrors(current => ({...current, [field]: null}));
  };

  const handleShippingSubmit = event => {
    event.preventDefault();
    const errors = validateAddress(shippingAddress);

    setAddressErrors(errors);
    if (Object.keys(errors).length > 0) return;

    goToStep('confirmation');
  };

  const handleConfirmOrder = () => {
    if (mutationLoading || orderSubmitted) return;
    const errors = validateAddress(shippingAddress);

    setAddressErrors(errors);
    if (Object.keys(errors).length > 0) {
      history.push(buildStepPath(giftId, 'shipping'));
      return;
    }

    let idempotencyKey;
    try {
      idempotencyKey = getOrderRequestKey(gift.id);
      setSubmissionError('');
    } catch {
      setSubmissionError(
        'Le stockage du navigateur doit être disponible pour envoyer la commande en sécurité.',
      );
      return;
    }
    dispatch(
      api.postGiftOrder({
        idempotencyKey,
        giftId: gift.id,
        variationRef: location.state?.variationRef || '',
        shippingAddress: {
          first_name: shippingAddress.firstName.trim(),
          last_name: shippingAddress.lastName.trim(),
          establishment: shippingAddress.establishment.trim(),
          address: shippingAddress.address.trim(),
          city: shippingAddress.city.trim(),
          postal_code: shippingAddress.postalCode.trim(),
          phone: normalizePhone(shippingAddress.phone),
        },
      }),
    );
  };

  if (giftsState.loading || profileState.loading || !giftsState.data || !profileState.data) {
    return null;
  }

  if (!gift) {
    return (
      <Container>
        <Header>
          <BackButton type="button" onClick={() => history.push('/gifts')}>
            <img src={arrowBackBlack} alt="Retour" />
          </BackButton>
        </Header>
        <EmptyState>Ce cadeau est introuvable.</EmptyState>
      </Container>
    );
  }

  const productName = sanitizeGiftLabel(gift.title?.rendered);

  return (
    <Container>
      {submissionError && <ErrorBox role="alert">{submissionError}</ErrorBox>}
      <Header>
        <BackButton type="button" onClick={handleBack}>
          <img src={arrowBackBlack} alt="Retour" />
        </BackButton>
      </Header>

      <ProgressWrapper>
        <ProgressTrack>
          <ProgressLine />
          <ProgressLineActive stepIndex={Math.max(stepIndex, 0)} />
          <ProgressDots>
            {steps.map((stepName, index) => (
              <ProgressDot key={stepName} active={index <= stepIndex} />
            ))}
          </ProgressDots>
        </ProgressTrack>
        <ProgressLabels>
          <ProgressLabel active={step === 'summary'}>
            Récapitulatif
            <br />
            de commande
          </ProgressLabel>
          <ProgressLabel active={step === 'shipping'}>
            Adresse
            <br />
            de livraison
          </ProgressLabel>
          <ProgressLabel active={step === 'confirmation'}>
            Validation
            <br />
            de commande
          </ProgressLabel>
        </ProgressLabels>
      </ProgressWrapper>

      <TitleBlock>
        <Title>
          {orderSubmitted
            ? 'Votre commande a bien été prise en compte'
            : stepTitles[step] || stepTitles.summary}
        </Title>
        <Divider />
        {orderSubmitted && (
          <ConfirmationText>
            Vous allez recevoir un email de confirmation d’ici quelques minutes.
          </ConfirmationText>
        )}
      </TitleBlock>

      {(step === 'summary' || step === 'confirmation') && (
        <ProductCard>
          <ProductImage src={gift.acf?.cadeaux_image} />
          <ProductDetails>
            <ProductName>{productName}</ProductName>
            <ProductMeta>
              x1
              {step === 'summary' && (
                <>
                  <br />
                  {requiredPoints.toLocaleString('fr-FR')} pts
                </>
              )}
            </ProductMeta>
          </ProductDetails>
        </ProductCard>
      )}

      {step === 'summary' && (
        <>
          <SummaryBlock>
            <SummaryRow bold>
              <SummaryLabel emphasis>Total</SummaryLabel>
              <SummaryValue>{requiredPoints.toLocaleString('fr-FR')} pts</SummaryValue>
            </SummaryRow>
            <SummaryRow topBorder>
              <SummaryLabel>Vous avez actuellement</SummaryLabel>
              <SummaryValue>{availablePoints.toLocaleString('fr-FR')} pts</SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Il vous restera</SummaryLabel>
              <SummaryValue>{remainingPoints.toLocaleString('fr-FR')} pts</SummaryValue>
            </SummaryRow>
          </SummaryBlock>

          <FullWidthButtons>
            <PrimaryButton type="button" color="white" onClick={() => goToStep('shipping')}>
              Valider la commande
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => history.push('/gifts')}>
              <span>Voir d’autres cadeaux</span>
            </SecondaryButton>
          </FullWidthButtons>
        </>
      )}

      {step === 'shipping' && (
        <FormBlock onSubmit={handleShippingSubmit}>
          <FieldGroup>
            <FieldLabel htmlFor="lastName">Nom *</FieldLabel>
            <TextField
              id="lastName"
              value={shippingAddress.lastName}
              maxLength={TEXT_MAX_LENGTH}
              onChange={handleAddressChange('lastName')}
              hasError={!!addressErrors.lastName}
            />
            {addressErrors.lastName && <FieldError>{addressErrors.lastName}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="firstName">Prénom *</FieldLabel>
            <TextField
              id="firstName"
              value={shippingAddress.firstName}
              maxLength={TEXT_MAX_LENGTH}
              onChange={handleAddressChange('firstName')}
              hasError={!!addressErrors.firstName}
            />
            {addressErrors.firstName && <FieldError>{addressErrors.firstName}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="establishment">Nom de votre établissement *</FieldLabel>
            <TextField
              id="establishment"
              value={shippingAddress.establishment}
              maxLength={TEXT_MAX_LENGTH}
              onChange={handleAddressChange('establishment')}
              hasError={!!addressErrors.establishment}
            />
            {addressErrors.establishment && <FieldError>{addressErrors.establishment}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="address">Adresse de livraison *</FieldLabel>
            <TextField
              id="address"
              value={shippingAddress.address}
              maxLength={TEXT_MAX_LENGTH}
              onChange={handleAddressChange('address')}
              hasError={!!addressErrors.address}
            />
            {addressErrors.address && <FieldError>{addressErrors.address}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="city">Ville de livraison *</FieldLabel>
            <TextField
              id="city"
              value={shippingAddress.city}
              maxLength={TEXT_MAX_LENGTH}
              onChange={handleAddressChange('city')}
              hasError={!!addressErrors.city}
            />
            {addressErrors.city && <FieldError>{addressErrors.city}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="postalCode">Code postal *</FieldLabel>
            <TextField
              id="postalCode"
              value={shippingAddress.postalCode}
              maxLength={POSTAL_CODE_LENGTH}
              inputMode="numeric"
              onChange={handleAddressChange('postalCode')}
              hasError={!!addressErrors.postalCode}
            />
            {addressErrors.postalCode && <FieldError>{addressErrors.postalCode}</FieldError>}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="phone">Téléphone du contact de livraison *</FieldLabel>
            <TextField
              id="phone"
              value={shippingAddress.phone}
              maxLength={PHONE_INPUT_MAX_LENGTH}
              inputMode="tel"
              onChange={handleAddressChange('phone')}
              hasError={!!addressErrors.phone}
            />
            {addressErrors.phone && <FieldError>{addressErrors.phone}</FieldError>}
          </FieldGroup>

          <RequiredHint>• champs obligatoires</RequiredHint>

          <ButtonGroup>
            <PrimaryButton type="submit" color="white">
              Valider ma commande
            </PrimaryButton>
            <SecondaryButton type="button" onClick={() => goToStep('summary')}>
              <span>Retour</span>
            </SecondaryButton>
          </ButtonGroup>
        </FormBlock>
      )}

      {step === 'confirmation' && (
        <>
          <AddressBlock compact={orderSubmitted}>
            {!orderSubmitted && mutationRequestError.length > 0 && (
              <ErrorBox>{mutationRequestError[0]}</ErrorBox>
            )}
            <AddressTitle>Adresse de livraison</AddressTitle>
            <AddressText>
              {shippingAddress.firstName} {shippingAddress.lastName}
              <br />
              {shippingAddress.establishment}
              <br />
              {shippingAddress.address}
              <br />
              {shippingAddress.postalCode} {shippingAddress.city}
            </AddressText>
          </AddressBlock>

          {orderSubmitted && (
            <SummaryBlock>
              <SummaryRow topBorder>
                <SummaryLabel>Votre nouveau solde</SummaryLabel>
                <SummaryValue>{balanceAfterOrder.toLocaleString('fr-FR')} pts</SummaryValue>
              </SummaryRow>
            </SummaryBlock>
          )}

          <ButtonGroup>
            {!orderSubmitted && (
              <PrimaryButton
                type="button"
                color="white"
                onClick={handleConfirmOrder}
                disabled={mutationLoading}>
                {mutationLoading ? 'Validation...' : 'Valider ma commande'}
              </PrimaryButton>
            )}
            {orderSubmitted && (
              <PrimaryButton type="button" color="white" onClick={() => history.push('/gifts')}>
                Commander un nouveau cadeau
              </PrimaryButton>
            )}
            <SecondaryButton type="button" onClick={() => history.push('/')}>
              <span>Retour à l’accueil</span>
            </SecondaryButton>
          </ButtonGroup>
        </>
      )}

      <FooterSpacer />
    </Container>
  );
};

export default GiftOrderFlow;
